import { motion } from 'framer-motion';
import type { RespondentBreakdown } from '../../lib/coach-dashboard';

interface CoachRespondentBreakdownProps {
  data: RespondentBreakdown;
  locale?: string;
}

export function CoachRespondentBreakdown({ data, locale }: CoachRespondentBreakdownProps) {
  const tr = locale === 'tr';
  const total = data.completed + data.pending;

  if (total === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">{tr ? 'Danışan Dağılımı' : 'Respondent Breakdown'}</h3>
        <p className="text-text-sm text-gray-40 text-center py-6">
          {tr ? 'Henüz danışan yok' : 'No respondents yet'}
        </p>
      </div>
    );
  }

  const completedPct = Math.round((data.completed / total) * 100);
  const pendingPct = 100 - completedPct;

  const segments = [
    { label: tr ? 'Tamamlanan' : 'Completed', value: data.completed, pct: completedPct, color: 'bg-success-40', textColor: 'text-success-70', bgColor: 'bg-success-5' },
    { label: tr ? 'Bekleyen' : 'Pending', value: data.pending, pct: pendingPct, color: 'bg-orange-40', textColor: 'text-orange-50', bgColor: 'bg-orange-10' },
  ];

  const details = [
    { label: tr ? 'YZ Analizi Oluşturuldu' : 'AI Analysis Generated', value: data.withAiAnalysis, total: data.completed, color: 'bg-teal-50' },
    { label: tr ? 'Tekrar Alındı' : 'Retakes Completed', value: data.withRetakes, total: data.completed, color: 'bg-brand-40' },
  ];

  return (
    <div className="freud-card-elevated p-6">
      <h3 className="text-text-xl text-gray-80 mb-5">{tr ? 'Danışan Dağılımı' : 'Respondent Breakdown'}</h3>

      <div className="flex h-3 rounded-full overflow-hidden mb-4">
        {segments.map(seg => (
          seg.value > 0 && (
            <motion.div
              key={seg.label}
              className={`${seg.color}`}
              initial={{ width: 0 }}
              animate={{ width: `${seg.pct}%` }}
              transition={{ duration: 0.6 }}
            />
          )
        ))}
      </div>

      <div className="flex gap-4 mb-6">
        {segments.map(seg => (
          <div key={seg.label} className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${seg.color}`} />
            <span className="text-text-sm text-gray-60">
              {seg.label}: <span className="font-semibold text-gray-80">{seg.value}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {details.map(detail => {
          const pct = detail.total > 0 ? Math.round((detail.value / detail.total) * 100) : 0;
          return (
            <div key={detail.label}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-text-sm text-gray-50">{detail.label}</span>
                <span className="text-text-xs text-gray-40">
                  {detail.value} / {detail.total} ({pct}%)
                </span>
              </div>
              <div className="h-2 bg-gray-10 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${detail.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
